// 游戏状态管理 - 响应式状态系统
// 管理玩家属性、游戏时间、进度等

export interface PlayerAttributes {
  // 五大核心属性
  happiness: number;    // 快乐 0-1000
  constitution: number; // 体质 0-1000
  wisdom: number;       // 智慧 0-1000
  charm: number;        // 魅力 0-1000
  reputation: number;   // 声望 -1000~1000
}

export interface DandanAttributes {
  intimacy: number;     // 亲密度 0-1000
  health: number;       // 健康 0-1000
  loyalty: number;      // 忠诚 0-1000
  skills: DandanSkills; // 技能
}

export interface DandanSkills {
  speed: number;        // 疾驰
  carry: number;        // 负重
  smell: number;        // 嗅觉
  alert: number;        // 预警
  dig: number;          // 挖掘
  swim: number;         // 游泳
  climb: number;        // 攀爬
  combat: number;       // 战斗
  search: number;       // 搜寻
  perform: number;      // 表演
}

export interface GameTime {
  year: number;         // 年份 (2016-2026)
  month: number;        // 月份 1-12
  day: number;          // 日期 1-31
  hour: number;         // 小时 0-23
  season: Season;       // 季节
  weather: Weather;     // 天气
}

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';
export type Weather = 'sunny' | 'cloudy' | 'rainy' | 'storm' | 'snowy' | 'foggy' | 'windy';

export interface StoryProgress {
  currentChapter: string;
  completedChapters: string[];
  flags: Record<string, boolean>;     // 剧情开关
  counters: Record<string, number>;   // 计数器
}

export interface Relationship {
  npcId: string;
  value: number;        // -100 ~ 100
}

export interface GameSaveData {
  // 元数据
  version: string;
  createdAt: number;
  updatedAt: number;
  playTime: number;
  
  // 玩家数据
  playerName: string;
  playerAge: number;
  attributes: PlayerAttributes;
  
  // 丹丹数据
  dandan: DandanAttributes;
  
  // 游戏时间
  gameTime: GameTime;
  
  // 进度
  progress: StoryProgress;
  
  // 关系
  relationships: Record<string, number>;
  
  // 位置
  currentScene: string;
  
  // 统计
  stats: GameStats;
}

export interface GameStats {
  totalClicks: number;
  totalCoins: number;
  itemsCollected: number;
  questsCompleted: number;
  playDays: number;
}

// 默认初始状态
export const DEFAULT_SAVE_DATA: GameSaveData = {
  version: '2.0.0',
  createdAt: Date.now(),
  updatedAt: Date.now(),
  playTime: 0,
  
  playerName: '欢欢',
  playerAge: 6,
  
  attributes: {
    happiness: 500,
    constitution: 300,
    wisdom: 200,
    charm: 300,
    reputation: 0,
  },
  
  dandan: {
    intimacy: 300,
    health: 500,
    loyalty: 400,
    skills: {
      speed: 10,
      carry: 10,
      smell: 20,
      alert: 15,
      dig: 5,
      swim: 0,
      climb: 5,
      combat: 10,
      search: 10,
      perform: 5,
    }
  },
  
  gameTime: {
    year: 2016,
    month: 6,
    day: 1,
    hour: 8,
    season: 'spring',
    weather: 'sunny',
  },
  
  progress: {
    currentChapter: 'ch1',
    completedChapters: [],
    flags: {},
    counters: {},
  },
  
  relationships: {
    grandma: 50,
    dandan: 60,
  },
  
  currentScene: 'village_home',
  
  stats: {
    totalClicks: 0,
    totalCoins: 0,
    itemsCollected: 0,
    questsCompleted: 0,
    playDays: 0,
  }
};

// 响应式状态管理类
export class GameState {
  private data: GameSaveData;
  private subscribers: Map<string, Set<(value: any) => void>> = new Map();
  private changeQueue: Map<string, any> = new Map();
  private isBatching: boolean = false;
  
  constructor(initialData: GameSaveData = DEFAULT_SAVE_DATA) {
    this.data = this.createReactiveCopy(initialData);
  }
  
  // 创建响应式代理
  private createReactiveCopy(data: any): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.createReactiveCopy(item));
    }
    
    const copy: any = {};
    for (const key in data) {
      copy[key] = this.createReactiveCopy(data[key]);
    }
    
    return copy;
  }
  
  // 获取状态
  get<K extends keyof GameSaveData>(key: K): GameSaveData[K] {
    return this.data[key];
  }
  
  // 获取嵌套值
  getPath(path: string): any {
    const keys = path.split('.');
    let value: any = this.data;
    
    for (const key of keys) {
      if (value === undefined || value === null) return undefined;
      value = value[key];
    }
    
    return value;
  }
  
  // 设置状态
  set<K extends keyof GameSaveData>(key: K, value: GameSaveData[K]): void {
    const oldValue = this.data[key];
    this.data[key] = this.createReactiveCopy(value);
    
    if (!this.isBatching) {
      this.notifyChange(key as string, oldValue, value);
    } else {
      this.changeQueue.set(key as string, value);
    }
  }
  
  // 设置嵌套值
  setPath(path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    let target: any = this.data;
    
    for (const key of keys) {
      if (target[key] === undefined) {
        target[key] = {};
      }
      target = target[key];
    }
    
    const oldValue = target[lastKey];
    target[lastKey] = this.createReactiveCopy(value);
    
    if (!this.isBatching) {
      this.notifyChange(path, oldValue, value);
    } else {
      this.changeQueue.set(path, value);
    }
  }
  
  // 批量更新
  batchUpdate(updates: Record<string, any>): void {
    this.isBatching = true;
    
    for (const [path, value] of Object.entries(updates)) {
      this.setPath(path, value);
    }
    
    this.isBatching = false;
    
    // 通知所有变更
    for (const [path, value] of this.changeQueue.entries()) {
      this.notifyChange(path, undefined, value);
    }
    this.changeQueue.clear();
  }
  
  // 订阅变更
  subscribe(path: string, callback: (value: any) => void): () => void {
    if (!this.subscribers.has(path)) {
      this.subscribers.set(path, new Set());
    }
    
    this.subscribers.get(path)!.add(callback);
    
    // 返回取消订阅函数
    return () => {
      this.subscribers.get(path)?.delete(callback);
    };
  }
  
  // 通知变更
  private notifyChange(path: string, oldValue: any, newValue: any): void {
    // 精确路径
    const callbacks = this.subscribers.get(path);
    if (callbacks) {
      callbacks.forEach(cb => cb(newValue));
    }
    
    // 父路径
    const parentPath = path.split('.').slice(0, -1).join('.');
    if (parentPath) {
      const parentCallbacks = this.subscribers.get(parentPath);
      if (parentCallbacks) {
        parentCallbacks.forEach(cb => cb(this.getPath(parentPath)));
      }
    }
    
    // 根路径
    const rootCallbacks = this.subscribers.get('*');
    if (rootCallbacks) {
      rootCallbacks.forEach(cb => cb({ path, oldValue, newValue }));
    }
  }
  
  // 增加属性值
  addToAttribute(attr: keyof PlayerAttributes, amount: number): void {
    const current = this.data.attributes[attr];
    let max = 1000;
    let min = 0;
    
    if (attr === 'reputation') {
      max = 1000;
      min = -1000;
    }
    
    const newValue = Math.max(min, Math.min(max, current + amount));
    this.setPath(`attributes.${attr}`, newValue);
  }
  
  // 增加丹丹属性
  addToDandanAttribute(attr: keyof DandanAttributes, amount: number): void {
    if (attr === 'skills') return; // 技能单独处理
    
    const current = this.data.dandan[attr] as number;
    const newValue = Math.max(0, Math.min(1000, current + amount));
    this.setPath(`dandan.${attr}`, newValue);
  }
  
  // 增加丹丹技能
  addToDandanSkill(skill: keyof DandanSkills, amount: number): void {
    const current = this.data.dandan.skills[skill];
    const newValue = Math.max(0, Math.min(100, current + amount));
    this.setPath(`dandan.skills.${skill}`, newValue);
  }
  
  // 设置剧情标志
  setFlag(flag: string, value: boolean = true): void {
    this.setPath(`progress.flags.${flag}`, value);
  }
  
  // 获取剧情标志
  getFlag(flag: string): boolean {
    return this.data.progress.flags[flag] || false;
  }
  
  // 增加计数器
  incrementCounter(counter: string, amount: number = 1): void {
    const current = this.data.progress.counters[counter] || 0;
    this.setPath(`progress.counters.${counter}`, current + amount);
  }
  
  // 推进游戏时间
  advanceTime(hours: number): void {
    let { year, month, day, hour, season } = this.data.gameTime;
    
    hour += hours;
    
    while (hour >= 24) {
      hour -= 24;
      day++;
      
      const daysInMonth = this.getDaysInMonth(year, month);
      if (day > daysInMonth) {
        day = 1;
        month++;
        
        if (month > 12) {
          month = 1;
          year++;
        }
        
        // 更新季节
        season = this.getSeason(month);
      }
    }
    
    this.setPath('gameTime', {
      ...this.data.gameTime,
      year,
      month,
      day,
      hour,
      season,
    });
  }
  
  // 获取月份天数
  private getDaysInMonth(year: number, month: number): number {
    const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 2 && this.isLeapYear(year)) {
      return 29;
    }
    return days[month - 1];
  }
  
  // 判断是否闰年
  private isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  }
  
  // 获取季节
  private getSeason(month: number): Season {
    if (month >= 3 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    if (month >= 9 && month <= 11) return 'autumn';
    return 'winter';
  }
  
  // 获取当前年龄
  getCurrentAge(): number {
    const birthYear = 2010;
    return this.data.gameTime.year - birthYear;
  }
  
  // 导出存档数据
  export(): GameSaveData {
    return this.createReactiveCopy(this.data);
  }
  
  // 导入存档数据
  import(data: GameSaveData): void {
    this.data = this.createReactiveCopy(data);
    this.subscribers.forEach(callbacks => {
      callbacks.forEach(cb => cb(this.data));
    });
  }
  
  // 获取属性等级名称
  getAttributeLevelName(attr: keyof PlayerAttributes): string {
    const value = this.data.attributes[attr];
    
    if (attr === 'happiness') {
      if (value <= 100) return '沮丧';
      if (value <= 300) return '低落';
      if (value <= 500) return '平静';
      if (value <= 700) return '愉悦';
      if (value <= 900) return '兴奋';
      return '狂喜';
    }
    
    if (attr === 'constitution') {
      if (value <= 100) return '虚弱';
      if (value <= 300) return '体弱';
      if (value <= 500) return '正常';
      if (value <= 700) return '健壮';
      if (value <= 900) return '强健';
      return '超凡';
    }
    
    if (attr === 'wisdom') {
      if (value <= 100) return '懵懂';
      if (value <= 300) return '初学';
      if (value <= 500) return '通晓';
      if (value <= 700) return '聪慧';
      if (value <= 900) return '睿智';
      return '超凡';
    }
    
    if (attr === 'charm') {
      if (value <= 100) return '普通';
      if (value <= 300) return '友善';
      if (value <= 500) return '亲和';
      if (value <= 700) return '迷人';
      if (value <= 900) return '有魅力';
      return '倾城';
    }
    
    if (attr === 'reputation') {
      if (value <= -500) return '恶名昭彰';
      if (value <= -100) return '声名狼藉';
      if (value <= 100) return '无名小卒';
      if (value <= 300) return '略有名气';
      if (value <= 500) return '声名鹊起';
      if (value <= 700) return '赫赫有名';
      if (value <= 900) return '名震一方';
      return '放牛山传奇';
    }
    
    return '未知';
  }
  
  // 获取丹丹亲密度等级
  getIntimacyLevel(): string {
    const value = this.data.dandan.intimacy;
    if (value <= 100) return '陌生';
    if (value <= 300) return '认识';
    if (value <= 500) return '熟悉';
    if (value <= 700) return '亲密';
    if (value <= 900) return '默契';
    return '灵魂伴侣';
  }
}

// 单例实例
let gameStateInstance: GameState | null = null;

export function getGameState(): GameState {
  if (!gameStateInstance) {
    gameStateInstance = new GameState();
  }
  return gameStateInstance;
}

export function initGameState(saveData?: GameSaveData): GameState {
  gameStateInstance = new GameState(saveData);
  return gameStateInstance;
}